import Base from '@studiometa/js-toolkit';

/**
 * @typedef {Object} FiltersRef
 * @property {HTMLElement} filters
 * @property {HTMLElement} reset
 * @property {HTMLElement[]} terms[]
 * @property {HTMLElement} content
 * @property {HTMLElement[]} open[]
 * @property {HTMLElement[]} close[]
 * @property {HTMLElement} pagination
 */

/**
 * @typedef {Object} FiltersInterface
 * @property {FiltersRef} $refs
 * @property {String} taxonomy
 * @property {String[]} values
 */

/**
 * Homepage class.
 */
export default class Filters extends Base {
  /**
   * Class config
   * @type {Object}
   */
  static config = {
    name: 'Filters',
    refs: ['filters', 'reset', 'terms[]', 'content', 'open[]', 'close[]', 'pagination'],
  };

  /**
   * Initialize the component's behavior.
   *
   * @this {Filters & FiltersInterface}
   */
  mounted() {
    this.taxonomy = this.$refs.filters.dataset.taxonomy;
    this.values = [];

    this.$refs.reset.addEventListener('click', () => {
      this.values = [];
      this.render();
      this.fetch();
    });

    this.$refs.terms?.forEach((term) => {
      term.addEventListener('click', () => {
        if (this.values.includes(term.dataset.value)) {
          this.values = this.values.filter((value) => value !== term.dataset.value);
        } else {
          this.values.push(term.dataset.value);
        }

        this.render();
        this.fetch();
      });
    });

    this.setupPagination();
  }

  /**
   * Render buttons.
   *
   * @this {Filters & FiltersInterface}
   */
  render() {
    this.$refs.reset.classList.remove('active');
    if (this.values.length === 0) {
      this.$refs.reset.classList.add('active');
    }

    this.$refs.terms?.forEach((term) => {
      term.classList.remove('active');
      if (this.values.includes(term.dataset.value)) {
        term.classList.add('active');
      }
    });
  }

  /**
   * Fetch and display results.
   *
   * @this {Filters & FiltersInterface}
   */
  fetch() {
    this.$refs.filters.classList.add('fetching');
    this.$refs.content.classList.add('fetching');

    const query = this.values.length
      ? `${this.$refs.filters.dataset.taxonomy}=${this.values.join(',')}`
      : '';

    const baseUrl = window.location.href.split('/page')[0];

    const queryString = `?${query}`;
    const url = this.values.length ? baseUrl.split('?')[0] + queryString : baseUrl.split('?')[0];

    window.history.replaceState({}, window.document.title, url);

    fetch(url)
      .then((res) => res.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const news = doc.querySelector('[data-ref="content"]');
        this.$refs.content.innerHTML = news.innerHTML;

        this.$refs.filters.classList.remove('fetching');
        this.$refs.content.classList.remove('fetching');

        this.setupPagination();
      });
  }

  /**
   * Fetch and append results.
   *
   * @this {Filters & FiltersInterface}
   *
   * @param {String} url
   */
  append(url) {
    this.$refs.filters.classList.add('fetching');
    this.$refs.content.classList.add('fetching');

    fetch(url)
      .then((res) => res.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const listing = this.$refs.content.querySelector('.listing');

        const news = doc.querySelector('[data-ref="content"]');
        const newsListing = news.querySelector('.listing');
        newsListing.innerHTML = listing.innerHTML + newsListing.innerHTML;

        this.$refs.content.innerHTML = news.innerHTML;

        this.$refs.filters.classList.remove('fetching');
        this.$refs.content.classList.remove('fetching');

        this.setupPagination();
      });
  }

  /**
   * Window scrolled event.
   *
   * @this {Filters & FiltersInterface}
   */
  scrolled() {
    this.$refs.filters.style.top = `${
      document.querySelector('.header').getBoundingClientRect().y +
      document.querySelector('.header').clientHeight
    }px`;
  }

  /**
   * Window scrolled event.
   *
   * @this {Filters & FiltersInterface}
   */
  setupPagination() {
    if (!this.$refs.pagination) return;

    this.$refs.pagination.addEventListener('click', (e) => {
      e.preventDefault();
      this.append(this.$refs.pagination.dataset.link);
    });
  }
}
